/* eslint-disable */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Empty, Input, Space, Spin, Tree } from 'antd'
import { BulbFilled, SearchOutlined } from '@ant-design/icons'
import {
  buildAntTreeKeyParentMap,
  collectAllTreeKeys,
  filterPermissionTreeData,
  mergeCheckedKeysWithAncestors,
  pruneCheckedKeysWithoutDescendants
} from '@/utils/rolePermissionTree'

import './RolePermissionTreeField.less'

const BRANCH_NODE_CLASS = 'permission-tree-node--branch'

/** 有子节点视为分类行：不展示勾选框、禁止勾选，仅叶子可点；与 checkStrictly + 合并祖先逻辑一致 */
function withParentBranchTreeMarkers(nodes) {
  if (!Array.isArray(nodes) || !nodes.length) return nodes
  const walk = (arr) =>
    arr.map((node) => {
      const rawChildren = node.children
      const hasChildren = Array.isArray(rawChildren) && rawChildren.length > 0
      const nextChildren = hasChildren ? walk(rawChildren) : rawChildren
      const className = [node.className, hasChildren ? BRANCH_NODE_CLASS : ''].filter(Boolean).join(' ')
      return {
        ...node,
        ...(hasChildren ? { disableCheckbox: true } : {}),
        ...(className ? { className } : {}),
        ...(nextChildren !== rawChildren ? { children: nextChildren } : {})
      }
    })
  return walk(nodes)
}

/** 非抽屉场景：按视口估算树区域最小高度（作 flex 子项占位，供 ResizeObserver 得到 Tree.height） */
function useFallbackTreeSlotMinHeight() {
  const [height, setHeight] = useState(320)

  useEffect(() => {
    const calc = () => {
      if (typeof window === 'undefined') return
      const { innerHeight: vh, innerWidth: vw } = window
      const reserve = vw < 576 ? 300 : vw < 992 ? 280 : 260
      const next = Math.min(520, Math.max(200, vh - reserve))
      setHeight(next)
    }
    calc()
    window.addEventListener('resize', calc)
    return () => window.removeEventListener('resize', calc)
  }, [])

  return height
}

/**
 * 角色权限勾选：搜索、全选/清空、虚拟滚动树，与 Form 受控对接
 *
 * @param {boolean} [contentHeight=false] — 为 true 时不设固定高度，树随展开内容增高（无虚拟滚动；数据量极大时慎用）
 */
export function PermissionTreeField({
  value,
  onChange,
  fullTreeData,
  loading,
  contentHeight = false
}) {
  const rootRef = useRef(null)
  const treeMeasureRef = useRef(null)
  const [drawerFieldMaxPx, setDrawerFieldMaxPx] = useState(null)
  const [treeScrollHeight, setTreeScrollHeight] = useState(320)
  const fallbackSlotMin = useFallbackTreeSlotMinHeight()

  const [search, setSearch] = useState('')
  const [expandedKeys, setExpandedKeys] = useState([])
  const treeHeight = contentHeight ? undefined : treeScrollHeight
  const checkedKeys = Array.isArray(value) ? value : []

  const checkedKeySet = useMemo(() => new Set(checkedKeys.map((k) => String(k))), [checkedKeys])

  /** 分类行：子级有选中时标题前「开灯」小图标（与 checkedKeys 含祖先 id 一致） */
  const titleRender = useCallback(
    (nodeData) => {
      const rawTitle = nodeData.title
      const titleNode = typeof rawTitle === 'function' ? rawTitle(nodeData) : rawTitle
      const key = nodeData.key != null ? String(nodeData.key) : ''
      const fromClass =
        typeof nodeData.className === 'string' && nodeData.className.includes(BRANCH_NODE_CLASS)
      const hasKids = Array.isArray(nodeData.children) && nodeData.children.length > 0
      const isBranch = fromClass || hasKids
      const showDescendantHint = isBranch && key && checkedKeySet.has(key)
      if (!showDescendantHint) return titleNode
      return (
        <span className="permissionTreeField-branchTitle">
          <BulbFilled
            className="permissionTreeField-branchTitle-icon"
            aria-hidden
            title="该分类下已有具体权限被选中"
          />
          <span className="permissionTreeField-branchTitle-text">{titleNode}</span>
        </span>
      )
    },
    [checkedKeySet]
  )

  const filteredTree = useMemo(
    () => filterPermissionTreeData(fullTreeData, search),
    [fullTreeData, search]
  )

  const treeDataForRender = useMemo(
    () => withParentBranchTreeMarkers(filteredTree),
    [filteredTree]
  )

  useEffect(() => {
    setExpandedKeys(collectAllTreeKeys(filteredTree))
  }, [filteredTree])

  /** 在抽屉内：整段字段（工具栏+树）最高不超过 body 内剩余空间 */
  useLayoutEffect(() => {
    if (contentHeight) return
    const root = rootRef.current
    if (!root) return

    const applyDrawerCap = () => {
      const body = root.closest('.ant-drawer-body')
      if (!body) {
        setDrawerFieldMaxPx(null)
        return
      }
      const bodyRect = body.getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()
      const padBottom = Number.parseFloat(getComputedStyle(body).paddingBottom) || 0
      const maxH = Math.max(200, Math.floor(bodyRect.bottom - padBottom - rootRect.top))
      setDrawerFieldMaxPx(maxH)
    }

    applyDrawerCap()
    const body = root.closest('.ant-drawer-body')
    const roBody =
      body && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(applyDrawerCap)
        : null
    if (body && roBody) roBody.observe(body)
    window.addEventListener('resize', applyDrawerCap)
    return () => {
      roBody?.disconnect()
      window.removeEventListener('resize', applyDrawerCap)
    }
  }, [contentHeight, filteredTree, fullTreeData.length, loading, search])

  /** 树滚动区域高度：随上方工具栏与抽屉可用高度变化 */
  useLayoutEffect(() => {
    if (contentHeight) return
    const el = treeMeasureRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const h = entries[0].contentRect.height
      setTreeScrollHeight(Math.max(120, Math.floor(h)))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [contentHeight, drawerFieldMaxPx, filteredTree, fullTreeData.length, loading])

  const allKeys = useMemo(() => collectAllTreeKeys(fullTreeData), [fullTreeData])

  /** 基于全量树解析父链；勾选子节点时合并进祖先 id（与接口 permissionIds 一致） */
  const keyParentMap = useMemo(
    () => buildAntTreeKeyParentMap(fullTreeData),
    [fullTreeData]
  )

  const emitCheckedKeys = (rawKeys) => {
    const next = Array.isArray(rawKeys) ? rawKeys : rawKeys?.checked
    const list = next || []
    const merged = mergeCheckedKeysWithAncestors(list, keyParentMap)
    onChange?.(pruneCheckedKeysWithoutDescendants(merged, fullTreeData))
  }

  const handleSelectAll = () => {
    onChange?.(allKeys)
  }

  const handleClear = () => {
    onChange?.([])
  }

  if (!fullTreeData.length && !loading) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="暂无权限数据，请确认已加载 /system/permissions"
      />
    )
  }

  const toolbarRow = (
    <div className="permissionTreeField-toolbar">
      <Input
        allowClear
        className="permissionTreeField-toolbarInput"
        placeholder="搜索权限名称或编码"
        prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
        style={{ flex: '1 1 160px', minWidth: 0, maxWidth: '100%' }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Space size={0} wrap style={{ flexShrink: 0 }}>
        <Button size="small" type="link" onClick={handleSelectAll}>
          全选
        </Button>
        <Button size="small" type="link" onClick={handleClear}>
          清空
        </Button>
      </Space>
    </div>
  )

  if (contentHeight) {
    return (
      <div className="permissionTreeField">
        {toolbarRow}
        <div className="permissionTreeField-treeWrap">
          <Spin spinning={loading}>
            <Tree
              blockNode
              checkable
              className="permissionTreeField-tree"
              // 父子独立勾选：若用默认 false，仅勾选父节点时 UI 会把其下「当前树里所有子节点」都当成已选，
              // 全量权限表新增子类型后也会被误显示为已选；与接口实际绑定的 id 列表不一致。
              checkStrictly
              checkedKeys={checkedKeys}
              expandedKeys={expandedKeys}
              height={treeHeight}
              virtual={treeHeight != null}
              onCheck={emitCheckedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              showLine
              titleRender={titleRender}
              treeData={treeDataForRender}
            />
          </Spin>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      className="permissionTreeField"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        ...(drawerFieldMaxPx != null
          ? { maxHeight: drawerFieldMaxPx, flex: 1 }
          : {})
      }}
    >
      {toolbarRow}
      <div
        ref={treeMeasureRef}
        className="permissionTreeField-treeWrap permissionTreeField-treeWrap--measure"
        style={{
          /** 抽屉内由 maxHeight 约束总高，须 minHeight:0 才能让本区参与 flex 收缩并得到真实高度 */
          minHeight: drawerFieldMaxPx != null ? 0 : fallbackSlotMin
        }}
      >
        <Spin spinning={loading} style={{ flex: 1, minHeight: 0 }}>
          <Tree
            blockNode
            checkable
            className="permissionTreeField-tree"
            checkStrictly
            checkedKeys={checkedKeys}
            expandedKeys={expandedKeys}
            height={treeHeight}
            virtual={treeHeight != null}
            onCheck={emitCheckedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            showLine
            titleRender={titleRender}
            treeData={treeDataForRender}
          />
        </Spin>
      </div>
    </div>
  )
}

PermissionTreeField.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  fullTreeData: PropTypes.array,
  loading: PropTypes.bool,
  contentHeight: PropTypes.bool
}
